'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import BlogCard from './components/BlogCard';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { blogService } from '@/lib/services/blogService';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  image: string;
  alt: string;
  readTime: string;
}

export default function Blog() {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const posts = await blogService.getAll();
        setBlogPosts(posts);
      } catch (error) {
        console.error('Failed to fetch blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  const categories = ['All', 'Saints', 'Prayers', 'Devotions', 'Church History', 'Spirituality'];

  const filteredPosts = blogPosts?.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post?.category === selectedCategory;
    const matchesSearch = post?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         post?.excerpt?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  const featuredPost = blogPosts[0] || null;

  return (
    <>
      <Header />
      <main className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-16 reveal">
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-foreground mb-4">
              SaintSeek Blog
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Stories of faith, prayers, and insights into the lives of the saints
            </p>
          </div>

          {/* Featured Post Hero */}
          <Link
            href={`/blog/${featuredPost?.id}`}
            className="block mb-16 reveal group">
            
            <div className="relative h-[500px] rounded-3xl overflow-hidden">
              <AppImage
                src={featuredPost?.image}
                alt={featuredPost?.alt}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              
              <div className="relative h-full flex items-end">
                <div className="p-12 w-full max-w-3xl">
                  <span className="inline-block bg-accent text-foreground px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                    Featured Post
                  </span>
                  <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4 group-hover:text-accent transition-colors">
                    {featuredPost?.title}
                  </h2>
                  <p className="text-lg text-white/90 mb-6 line-clamp-2">
                    {featuredPost?.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-white/80">
                    <span>{featuredPost?.author}</span>
                    <span>•</span>
                    <span>{featuredPost?.date}</span>
                    <span>•</span>
                    <span>{featuredPost?.readTime}</span>
                  </div>
                </div>
              </div>
            </div>
          </Link>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Blog Posts - Main Column */}
            <div className="lg:col-span-3 space-y-12">
              {/* Category Filters */}
              <div className="flex flex-wrap gap-3 reveal">
                {categories.map((category) =>
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category ?
                  'bg-primary text-white' : 'glass-card border border-border text-foreground hover:border-primary'}`
                  }>
                  
                    {category} ({filteredPosts?.length})
                  </button>
                )}
              </div>

              {/* Posts Grid */}
              <div className="grid md:grid-cols-2 gap-8 reveal">
                {filteredPosts?.slice(1).map((post) =>
                <BlogCard key={post.id} post={post} />
                )}
              </div>

              {/* Load More */}
              <div className="text-center">
                <button className="inline-flex items-center gap-2 glass-card border border-border px-8 py-4 rounded-xl font-medium hover:border-primary hover:text-primary transition-all">
                  <span>Load More Posts</span>
                  <Icon name="ArrowDownIcon" size={20} />
                </button>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Search */}
              <div className="glass-card p-6 rounded-2xl border border-border reveal">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Search Blog
                </h3>
                <div className="relative">
                  <Icon
                    name="MagnifyingGlassIcon"
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search articles..."
                    className="w-full h-12 bg-muted border border-border rounded-xl pl-12 pr-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors" />
                  
                </div>
              </div>

              {/* Popular Posts */}
              <div className="glass-card p-6 rounded-2xl border border-border reveal">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Popular Posts
                </h3>
                <div className="space-y-4">
                  {popularPosts.map((post, index) =>
                  <div key={post.id} className="flex gap-3 group cursor-pointer">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          {post.views} views
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Newsletter */}
              <div className="glass-card p-6 rounded-2xl border-2 border-primary/20 reveal">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4">
                  <Icon name="EnvelopeIcon" size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Weekly Newsletter
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Get saint stories and prayers delivered to your inbox
                </p>
                <input
                  type="email"
                  placeholder="your@email.com"
                  className="w-full h-12 bg-muted border border-border rounded-xl px-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors mb-3" />
                
                <button className="w-full bg-primary text-white px-4 py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors">
                  Subscribe
                </button>
              </div>

              {/* Categories Widget */}
              <div className="glass-card p-6 rounded-2xl border border-border reveal">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Categories
                </h3>
                <div className="space-y-2">
                  {categories.slice(1).map((category) =>
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className="w-full flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted transition-colors text-left">
                    
                      <span className="text-sm text-foreground">{category}</span>
                      <span className="text-xs text-muted-foreground">
                        {filteredPosts?.length}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}